import React from 'react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { changelogData, ChangelogEntry } from '@/data/changelog';
import { 
  CheckCircle, 
  Bug, 
  TrendingUp, 
  RefreshCw, 
  X,
  Calendar,
  Tag
} from 'lucide-react';

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedVersion?: string;
}

const getTypeIcon = (type: ChangelogEntry['type']) => {
  switch (type) {
    case 'major':
      return <TrendingUp className="w-4 h-4 text-red-500" />;
    case 'minor':
      return <CheckCircle className="w-4 h-4 text-blue-500" />;
    case 'patch':
      return <Bug className="w-4 h-4 text-green-500" />;
    default:
      return <Tag className="w-4 h-4 text-gray-500" />;
  }
};

const getTypeColor = (type: ChangelogEntry['type']) => {
  switch (type) {
    case 'major':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'minor':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'patch':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getTypeLabel = (type: ChangelogEntry['type']) => {
  switch (type) {
    case 'major':
      return 'Atualização Principal';
    case 'minor':
      return 'Nova Funcionalidade';
    case 'patch':
      return 'Correção';
    default:
      return 'Atualização';
  }
};

export const ChangelogModal: React.FC<ChangelogModalProps> = ({
  isOpen,
  onClose,
  selectedVersion
}) => {
  const displayData = selectedVersion 
    ? changelogData.filter(entry => entry.version === selectedVersion)
    : changelogData;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Histórico de Versões"
      size="lg"
    >
      <div className="space-y-6">
        {displayData.map((entry) => (
          <div key={entry.version} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
            {/* Header da versão */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                {getTypeIcon(entry.type)}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Versão {entry.version}
                  </h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-slate-400">
                    <Calendar className="w-4 h-4" />
                    <span>{entry.date}</span>
                  </div>
                </div>
              </div>
              <Badge className={getTypeColor(entry.type)}>
                {getTypeLabel(entry.type)}
              </Badge>
            </div>

            {/* Mudanças */}
            <div className="space-y-4">
              {/* Novas funcionalidades */}
              {entry.changes.added && entry.changes.added.length > 0 && (
                <div>
                  <h4 className="flex items-center space-x-2 text-sm font-medium text-green-700 dark:text-green-400 mb-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Novas Funcionalidades</span>
                  </h4>
                  <ul className="space-y-1 ml-6">
                    {entry.changes.added.map((item, idx) => (
                      <li key={idx} className="text-sm text-gray-700 dark:text-slate-300 flex items-start">
                        <span className="text-green-500 mr-2">+</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Correções */}
              {entry.changes.fixed && entry.changes.fixed.length > 0 && (
                <div>
                  <h4 className="flex items-center space-x-2 text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                    <Bug className="w-4 h-4" />
                    <span>Correções</span>
                  </h4>
                  <ul className="space-y-1 ml-6">
                    {entry.changes.fixed.map((item, idx) => (
                      <li key={idx} className="text-sm text-gray-700 dark:text-slate-300 flex items-start">
                        <span className="text-red-500 mr-2">🐛</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Melhorias */}
              {entry.changes.improved && entry.changes.improved.length > 0 && (
                <div>
                  <h4 className="flex items-center space-x-2 text-sm font-medium text-blue-700 dark:text-blue-400 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <span>Melhorias</span>
                  </h4>
                  <ul className="space-y-1 ml-6">
                    {entry.changes.improved.map((item, idx) => (
                      <li key={idx} className="text-sm text-gray-700 dark:text-slate-300 flex items-start">
                        <span className="text-blue-500 mr-2">⚡</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Mudanças */}
              {entry.changes.changed && entry.changes.changed.length > 0 && (
                <div>
                  <h4 className="flex items-center space-x-2 text-sm font-medium text-orange-700 dark:text-orange-400 mb-2">
                    <RefreshCw className="w-4 h-4" />
                    <span>Mudanças</span>
                  </h4>
                  <ul className="space-y-1 ml-6">
                    {entry.changes.changed.map((item, idx) => (
                      <li key={idx} className="text-sm text-gray-700 dark:text-slate-300 flex items-start">
                        <span className="text-orange-500 mr-2">🔄</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-slate-400">
            <button
              onClick={onClose}
              className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              <X className="w-4 h-4" />
              <span>Fechar</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
